import { Link } from 'gatsby';
import twMerge from 'twMerge';

import breadcrumbsStyles, { breadcrumbsChevron, breadcrumbsLink } from 'molecules/Breadcrumbs/Breadcrumbs.styles';
import Icon from 'molecules/Icon';

import parseUrl from 'utils/parseUrl';

import type { VariantProps } from 'class-variance-authority';
import type { FC } from 'react';

export interface BreadcrumbsProps {
  breadcrumbs: {
    label?: string;
    link?: string;
  }[];
  isDarkTheme: boolean;
  className?: string;
  visible: boolean | null | undefined;
  ignoreLastCrumbLogic: boolean;
}
const specificBreadcrumb1 = { current: 'Aspire Vs Boss Lm', update: 'Aspire vs. Boss LM' };
const specificBreadcrumb2 = { current: 'Aspire Vs Asset', update: 'Aspire vs Asset' };
const getLinkContent = (link: string) =>
  link
    .split('/')
    .slice(-1)[0]
    .split('-')
    .map(str => str && str[0].toUpperCase() + str.substring(1).toLowerCase())
    .join(' ');

const Breadcrumbs: FC<BreadcrumbsProps> = ({
  breadcrumbs,
  isDarkTheme,
  className,
  visible,
  ignoreLastCrumbLogic = false,
  ...props
}) => {
  const { Component: homeAs, ...homeUrlProps } = parseUrl('/');
  const HomeComponent = homeAs === 'div' ? 'button' : 'a';
  const homeLinkType = ((isDarkTheme ? 'dark' : 'light') + 'Standard') as VariantProps<typeof breadcrumbsLink>['type'];

  const checkSpecificBreadCrumb = (breadcrumb: string) => {
    if (breadcrumb === specificBreadcrumb1.current) {
      return specificBreadcrumb1.update;
    } else if (breadcrumb === specificBreadcrumb2.current) {
      return specificBreadcrumb2.update;
    }

    return breadcrumb;
  };

  return (
    <div
      data-testid="breadcrumbs"
      className={twMerge(breadcrumbsStyles(), className ? ` ${className}` : '')}
      {...props}
    >
      {visible && (
        <>
          <HomeComponent className={twMerge(breadcrumbsLink({ type: homeLinkType }))} {...homeUrlProps}>
            <Icon icon="home" size={20} />
          </HomeComponent>
          {breadcrumbs &&
            breadcrumbs.map(({ label, link }, index) => {
              const linkContent = label || getLinkContent(link || '/');
              const isLastEntry = index === breadcrumbs.length - 1;
              const getStatus = () => {
                if (ignoreLastCrumbLogic) return 'Standard';
                if (isLastEntry) return 'Active';
                return 'Standard';
              };
              const linkType = ((isDarkTheme ? 'dark' : 'light') + getStatus()) as VariantProps<
                typeof breadcrumbsLink
              >['type'];

              return (
                <div key={index} className="flex items-start justify-center gap-2">
                  <Icon
                    className={twMerge(breadcrumbsChevron({ isDarkTheme }))}
                    icon="chevron-right"
                    size={16}
                    aria-hidden={true}
                  />
                  <Link
                    key={link}
                    to={link}
                    className={twMerge(
                      breadcrumbsLink({ type: linkType, disabled: ignoreLastCrumbLogic ? false : isLastEntry }),
                    )}
                  >
                    {checkSpecificBreadCrumb(linkContent)}
                  </Link>
                </div>
              );
            })}
        </>
      )}
    </div>
  );
};

export default Breadcrumbs;
